import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  DirectionsBus,
  AccessTime,
  Person,
  People,
  Warning,
  CheckCircle,
  LocationOn,
  AcUnit,
  Whatshot,
  Delete,
} from '@mui/icons-material';
import { doc, getDoc, collection, query, where, getDocs, onSnapshot, deleteDoc } from 'firebase/firestore';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { MainLayout } from '../components/layout/MainLayout';
import { db } from '../lib/firebase';
import { Operation, StopRecord, StationLog } from '../types/operation';
import { RouteMonthlyCheckin } from '../types/boarding';
import { useAuth } from '../contexts/AuthContext';

const DELETE_WAIT_MINUTES = 5;

const isDeleteEligibleOperationData = (data: {
  status?: string;
  lastUpdateTime?: { toDate?: () => Date } | null;
}, now = Date.now()) => {
  if (data.status !== 'READY') {
    return false;
  }

  if (!data.lastUpdateTime?.toDate) {
    return true;
  }

  const minutesSinceUpdate =
    (now - data.lastUpdateTime.toDate().getTime()) / (1000 * 60);

  return minutesSinceUpdate >= DELETE_WAIT_MINUTES;
};

export const OperationDetailPage: React.FC = () => {
  const { operationId } = useParams<{ operationId: string }>();
  const navigate = useNavigate();
  const { adminType } = useAuth();
  const [operation, setOperation] = useState<Operation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!operationId) return;

    setLoading(true);
    console.log('Subscribing to operation:', operationId);

    // 운행 기록 실시간 구독
    const unsubscribe = onSnapshot(
      doc(db, 'operations', operationId),
      async (operationDoc) => {
        if (!operationDoc.exists()) {
          console.error('Operation not found');
          setOperation(null);
          setLoading(false);
          return;
        }

        console.log('Operation updated:', operationDoc.id);

        const docData = operationDoc.data();
        const operationDate = operationId.split('_')[0];

        const operationData: Operation = {
          id: operationDoc.id,
          operationDate,
          routeId: docData.routeId || '',
          routeName: docData.routeName || '',
          vehicleNumber: docData.vehicleNumber || '',
          driverName: docData.driverName || '',
          status: docData.status || '',
          lastUpdateTime: docData.lastUpdateTime,
          startTime: docData.startTime,
          endTime: docData.endTime,
          currentStopIndex: docData.currentStopIndex || 0,
          totalStops: docData.totalStops || 0,
          isDelayed: docData.isDelayed || false,
          delayMinutes: docData.delayMinutes || 0,
          totalPassengers: docData.totalPassengers || 0,
          isHot: docData.isHot || 0,
          isCold: docData.isCold || 0,
          isDanger: docData.isDanger || 0,
          stopRecords: docData.stopRecords || [],
          stationLogs: docData.stationLogs || [],
        };

        setOperation(operationData);

        // 승차 인원 데이터 조회
        await enrichOperationWithCheckinData(operationData);
        setOperation({ ...operationData }); // 상태 업데이트
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to operation:', error);
        setLoading(false);
      }
    );

    // 구독 해제 함수 반환
    return () => {
      console.log('Unsubscribing from operation:', operationId);
      unsubscribe();
    };
  }, [operationId]);

  useEffect(() => {
    if (!operation || isDeleteEligibleOperationData(operation, now)) {
      return;
    }

    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 10000);

    return () => window.clearInterval(timer);
  }, [operation, now]);

  // 승차 인원 데이터 조회 및 통합
  const enrichOperationWithCheckinData = async (operation: Operation) => {
    try {
      // 운행 날짜에서 년월 추출 (YYYY-MM-DD -> YYYYMM)
      const yearMonth = operation.operationDate.replace(/-/g, '').substring(0, 6);

      // route_monthly_checkins에서 해당 노선의 해당 월 데이터 조회
      const checkinQuery = query(
        collection(db, 'route_monthly_checkins'),
        where('year_month', '==', yearMonth)
      );

      const checkinSnapshot = await getDocs(checkinQuery);

      checkinSnapshot.docs.forEach((doc) => {
        const routeId = doc.id.split('_')[0];

        if (routeId === operation.routeId) {
          const checkinData = doc.data() as RouteMonthlyCheckin;

          if (checkinData.checkins) {
            // 해당 날짜의 승차 기록만 필터링
            const dateCheckins = checkinData.checkins.filter((checkin) => {
              if (checkin.timestamp && checkin.timestamp.toDate) {
                const checkinDate = format(checkin.timestamp.toDate(), 'yyyy-MM-dd');
                return checkinDate === operation.operationDate;
              }
              return false;
            });

            operation.totalPassengers = dateCheckins.length;

            // 정류장별 승차 인원 집계
            // stationLogs가 있는 경우 (우선순위)
            if (operation.stationLogs && operation.stationLogs.length > 0) {
              operation.stationLogs.forEach((log) => {
                const stopCheckins = dateCheckins.filter(
                  (checkin) => checkin.station_name === log.stationName
                );
                // expectedPassengers를 실제 승차 인원으로 설정
                if (!log.expectedPassengers || log.expectedPassengers === 0) {
                  log.expectedPassengers = stopCheckins.length;
                }
              });
            }
            // stopRecords가 있는 경우 (구버전 호환)
            else if (operation.stopRecords && operation.stopRecords.length > 0) {
              operation.stopRecords.forEach((stop) => {
                const stopCheckins = dateCheckins.filter(
                  (checkin) => checkin.station_name === stop.stopName
                );
                stop.passengerCount = stopCheckins.length;
              });
            }
          }
        }
      });

      if (!operation.totalPassengers) {
        operation.totalPassengers = 0;
      }
    } catch (error) {
      console.error('Error fetching checkin data for operation detail:', error);
      operation.totalPassengers = 0;
    }
  };

  const getRouteAdminFilter = () => {
    switch (adminType) {
      case 'route1':
        return '강서구청';
      case 'route2':
        return '하단';
      default:
        return null;
    }
  };

  const isRouteAdmin = adminType === 'route1' || adminType === 'route2';

  const isRouteAdminOperation = (targetOperation: Operation) => {
    const routeFilter = getRouteAdminFilter();
    if (!routeFilter) {
      return false;
    }

    const routeName = targetOperation.routeName || targetOperation.routeId;
    return routeName.includes(routeFilter);
  };

  const getDeleteBlockReasons = (targetOperation: Operation, nowValue = Date.now()) => {
    const reasons: string[] = [];

    if (!isRouteAdminOperation(targetOperation)) {
      reasons.push('담당 노선의 운행 기록만 취소할 수 있습니다.');
    }

    if (targetOperation.status !== 'READY') {
      reasons.push(`운행 상태가 READY일 때만 취소할 수 있습니다. 현재 상태: ${targetOperation.status || '-'}`);
    } else if (targetOperation.lastUpdateTime?.toDate) {
      const minutesSinceUpdate =
        (nowValue - targetOperation.lastUpdateTime.toDate().getTime()) / (1000 * 60);

      if (minutesSinceUpdate < DELETE_WAIT_MINUTES) {
        const remainingMinutes = Math.ceil(DELETE_WAIT_MINUTES - minutesSinceUpdate);
        reasons.push(`마지막 상태 업데이트 후 ${DELETE_WAIT_MINUTES}분이 지나야 합니다. 남은 시간: 약 ${remainingMinutes}분`);
      }
    }

    return reasons;
  };

  const deleteBlockReasons = operation ? getDeleteBlockReasons(operation, now) : [];
  const canDeleteOperation = operation !== null && deleteBlockReasons.length === 0;
  const shouldShowDeleteButton = isRouteAdmin;

  const handleDeleteClick = () => {
    if (!canDeleteOperation) {
      return;
    }

    setError('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!operationId || !operation) return;

    setDeleting(true);
    setError('');

    try {
      const operationRef = doc(db, 'operations', operationId);
      const latestDoc = await getDoc(operationRef);

      if (!latestDoc.exists()) {
        navigate('/operation');
        return;
      }

      const latestData = latestDoc.data();
      const routeName = latestData.routeName || operation.routeName || latestData.routeId || operation.routeId;
      const latestOperationForAuth = {
        ...operation,
        routeId: latestData.routeId || operation.routeId,
        routeName,
        status: latestData.status || '',
        lastUpdateTime: latestData.lastUpdateTime,
      };
      const latestDeleteBlockReasons = getDeleteBlockReasons(latestOperationForAuth, Date.now());

      if (latestDeleteBlockReasons.length > 0) {
        setError(`삭제 조건을 만족하지 않는 운행 기록입니다. ${latestDeleteBlockReasons.join(' ')}`);
        setDeleteDialogOpen(false);
        return;
      }

      await deleteDoc(operationRef);
      navigate('/operation');
    } catch (err) {
      console.error('Error deleting operation:', err);
      setError('운행 기록 삭제에 실패했습니다. 다시 시도해주세요.');
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </MainLayout>
    );
  }

  if (!operation) {
    return (
      <MainLayout>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="text.secondary">
            운행 기록을 찾을 수 없습니다.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/operation')}
            sx={{ mt: 3 }}
          >
            목록으로 돌아가기
          </Button>
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Box>
        {/* 헤더 */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={() => navigate('/operation')}
            >
              목록으로
            </Button>
            <Box>
              <Typography variant="h4" component="h1" fontWeight={700}>
                운행 기록 상세
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {operation.routeName || operation.routeId} - {format(new Date(operation.operationDate), 'yyyy년 MM월 dd일 (E)', { locale: ko })}
              </Typography>
            </Box>
          </Box>
          {shouldShowDeleteButton && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteClick}
                disabled={!canDeleteOperation || deleting}
              >
                운행 취소
              </Button>
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {shouldShowDeleteButton && deleteBlockReasons.length > 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              운행 취소가 비활성화된 이유
            </Typography>
            {deleteBlockReasons.map((reason) => (
              <Typography key={reason} variant="body2">
                - {reason}
              </Typography>
            ))}
          </Alert>
        )}

        {/* 기본 정보 카드 */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 3, mb: 3 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <DirectionsBus sx={{ color: '#667eea' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    노선
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  {operation.routeName || operation.routeId}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {operation.vehicleNumber}
                </Typography>
              </CardContent>
            </Card>


            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Person sx={{ color: '#667eea' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    운전자
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  {operation.driverName || '-'}
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <People sx={{ color: '#667eea' }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    총 승차 인원
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={600}>
                  {operation.totalPassengers || 0}명
                </Typography>
              </CardContent>
            </Card>


            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  {operation.status === 'COMPLETED' ? (
                    <CheckCircle sx={{ color: '#10b981' }} />
                  ) : (
                    <AccessTime sx={{ color: '#667eea' }} />
                  )}
                  <Typography variant="subtitle2" color="text.secondary">
                    운행 상태
                  </Typography>
                </Box>
                <Chip
                  label={
                    operation.status === 'COMPLETED' || operation.status === 'COMPLETE'
                      ? '운행 종료'
                      : operation.status === 'IN_PROGRESS'
                      ? '운행 중'
                      : operation.status
                  }
                  color={operation.status === 'COMPLETED' || operation.status === 'COMPLETE' ? 'success' : 'primary'}
                />
              </CardContent>
            </Card>
        </Box>

        {/* 운행 시간 및 민원 정보 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            운행 정보
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTime sx={{ fontSize: 20, color: '#667eea' }} />
                <Typography variant="body2" color="text.secondary">
                  운행 시작
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {operation.startTime
                  ? format(operation.startTime.toDate(), 'HH:mm:ss')
                  : '-'}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTime sx={{ fontSize: 20, color: '#667eea' }} />
                <Typography variant="body2" color="text.secondary">
                  운행 종료
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {operation.endTime
                  ? format(operation.endTime.toDate(), 'HH:mm:ss')
                  : '-'}
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Warning sx={{ fontSize: 20, color: operation.isDelayed ? '#f59e0b' : '#10b981' }} />
                <Typography variant="body2" color="text.secondary">
                  지연 여부
                </Typography>
              </Box>
              {operation.isDelayed ? (
                <Chip
                  icon={<Warning />}
                  label={`${operation.delayMinutes || 0}분 지연`}
                  color="warning"
                  size="small"
                />
              ) : (
                <Typography variant="body1" fontWeight={600} color="#10b981">
                  정상 운행
                </Typography>
              )}
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Whatshot sx={{ fontSize: 20, color: '#ef4444' }} />
                <Typography variant="body2" color="text.secondary">
                  더움 민원
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {operation.isHot}건
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AcUnit sx={{ fontSize: 20, color: '#3b82f6' }} />
                <Typography variant="body2" color="text.secondary">
                  추움 민원
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {operation.isCold}건
              </Typography>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Warning sx={{ fontSize: 20, color: '#f59e0b' }} />
                <Typography variant="body2" color="text.secondary">
                  안전운행 민원
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {operation.isDanger}건
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 정류장별 운행 기록 */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            정류장별 운행 기록
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {operation.stationLogs && operation.stationLogs.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="60px">순번</TableCell>
                    <TableCell>정류장명</TableCell>
                    <TableCell align="center">예정 시간</TableCell>
                    <TableCell align="center">실제 도착</TableCell>
                    <TableCell align="center">지연 시간</TableCell>
                    <TableCell align="center">이용 인원</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operation.stationLogs.map((log: StationLog, index: number) => {
                    const isDelayed = log.delayMinutes && log.delayMinutes > 0;
                    const isEarly = log.delayMinutes && log.delayMinutes < 0;

                    return (
                      <TableRow key={log.stationId} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocationOn sx={{ fontSize: 18, color: '#667eea' }} />
                            <Typography variant="body2" fontWeight={600}>
                              {index + 1}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {log.stationName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600}>
                            {log.scheduledTime}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {log.actualArrivalTime
                              ? format(log.actualArrivalTime.toDate(), 'HH:mm:ss')
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {log.status === 'skipped' ? (
                            <Chip label="통과" color="default" size="small" />
                          ) : log.delayMinutes !== undefined && log.delayMinutes !== 0 ? (
                            <Chip
                              icon={isDelayed ? <Warning /> : undefined}
                              label={
                                isDelayed
                                  ? `+${log.delayMinutes}분`
                                  : isEarly
                                  ? `${log.delayMinutes}분`
                                  : '정시'
                              }
                              color={
                                isDelayed
                                  ? 'warning'
                                  : isEarly
                                  ? 'info'
                                  : 'success'
                              }
                              size="small"
                            />
                          ) : (
                            <Chip label="정시" color="success" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <People sx={{ fontSize: 16, color: '#667eea' }} />
                            <Typography variant="body2" fontWeight={600}>
                              {log.expectedPassengers || 0}명
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : operation.stopRecords && operation.stopRecords.length > 0 ? (
            // 구버전 stopRecords 표시 (호환성)
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width="60px">순번</TableCell>
                    <TableCell>정류장명</TableCell>
                    <TableCell align="center">도착 시간</TableCell>
                    <TableCell align="center">출발 시간</TableCell>
                    <TableCell align="center">탑승 인원</TableCell>
                    <TableCell align="center">상태</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operation.stopRecords.map((stop: StopRecord, index: number) => (
                    <TableRow key={stop.stopId} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOn sx={{ fontSize: 18, color: '#667eea' }} />
                          <Typography variant="body2" fontWeight={600}>
                            {index + 1}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {stop.stopName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {stop.arrivalTime
                            ? format(stop.arrivalTime.toDate(), 'HH:mm:ss')
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {stop.departureTime
                            ? format(stop.departureTime.toDate(), 'HH:mm:ss')
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <People sx={{ fontSize: 16, color: '#667eea' }} />
                          <Typography variant="body2" fontWeight={600}>
                            {stop.passengerCount || 0}명
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {stop.isSkipped ? (
                          <Chip label="통과" size="small" color="default" />
                        ) : (
                          <Chip label="정차" size="small" color="success" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                정류장별 상세 기록이 없습니다.
              </Typography>
            </Box>
          )}
        </Paper>

        <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
          <DialogTitle>운행 취소</DialogTitle>
          <DialogContent>
            <DialogContentText>
              이 운행 기록을 삭제 처리하시겠습니까?
              <br />
              삭제된 운행 기록은 복구할 수 없습니다.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={deleting}>
              취소
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : undefined}
            >
              삭제
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};
